import { supabase } from '../lib/supabase';

// 배치 처리를 위한 설정
const BATCH_SIZE = 10; // 최대 배치 크기
const BATCH_INTERVAL = 5000; // 배치 처리 간격 (5초)
let pendingUpdates = []; // 대기 중인 업데이트 목록
let batchTimeout = null; // 배치 처리 타이머

// 배치 처리 함수
const processBatchUpdates = async () => {
  if (pendingUpdates.length === 0) return;
  
  try {
    const updatesCopy = [...pendingUpdates];
    pendingUpdates = []; // 배치 처리할 항목 초기화
    
    // 사용자별 업데이트 그룹화
    const userUpdates = {};
    
    for (const update of updatesCopy) {
      const { userId, type, data } = update;
      
      if (!userUpdates[userId]) {
        userUpdates[userId] = { 
          quizCount: 0, 
          correctCount: 0, 
          wrongCount: 0,
          studyTime: 0 
        };
      }
      
      // 업데이트 유형별 처리
      if (type === 'quiz') {
        userUpdates[userId].quizCount++;
        if (data.isCorrect) {
          userUpdates[userId].correctCount++;
        } else {
          userUpdates[userId].wrongCount++;
        }
      } else if (type === 'study_time') {
        userUpdates[userId].studyTime += data.minutes;
      }
    }
    
    // 각 사용자별 통계 업데이트
    const promises = Object.entries(userUpdates).map(async ([userId, updates]) => {
      try {
        // 기존 데이터 가져오기
        const { data: statsData, error } = await supabase
          .from('user_learning_stats')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle();
          
        if (error) throw error;
        
        // 데이터가 없으면 기본 데이터 생성
        if (!statsData) {
          await createDefaultStats(userId);
          return;
        }
        
        // 업데이트할 필드
        const updateData = {};
        
        if (updates.quizCount > 0) {
          updateData.total_questions = statsData.total_questions + updates.quizCount;
          updateData.correct_count = statsData.correct_count + updates.correctCount;
          updateData.wrong_count = statsData.wrong_count + updates.wrongCount;
        }
        
        if (updates.studyTime > 0) {
          updateData.total_study_time = statsData.total_study_time + updates.studyTime;
        }
        
        if (Object.keys(updateData).length > 0) {
          updateData.updated_at = new Date().toISOString();
          
          // 오늘의 학습 기록 업데이트
          const today = new Date().getDay(); // 0(일) ~ 6(토)
          let recentDays = [];
          
          try {
            recentDays = Array.isArray(statsData.recent_study_days) 
              ? [...statsData.recent_study_days] 
              : Array(7).fill(0);
          } catch (e) {
            recentDays = Array(7).fill(0);
          }
          
          recentDays[today] = (recentDays[today] || 0) + 1;
          updateData.recent_study_days = recentDays;
          
          // 데이터베이스 업데이트
          await supabase
            .from('user_learning_stats')
            .update(updateData)
            .eq('user_id', userId);
        }
      } catch (userError) {
        console.error(`사용자 ${userId} 통계 업데이트 실패:`, userError);
      }
    });
    
    await Promise.all(promises);
    
  } catch (error) {
    console.error('배치 처리 중 오류 발생:', error);
  }
};

// 배치 처리 예약
const scheduleBatchProcessing = () => {
  if (batchTimeout) clearTimeout(batchTimeout);
  
  // 배치 크기가 임계값에 도달하면 즉시 처리
  if (pendingUpdates.length >= BATCH_SIZE) {
    processBatchUpdates();
  } else {
    // 아니면 일정 시간 후 처리
    batchTimeout = setTimeout(processBatchUpdates, BATCH_INTERVAL);
  }
};

// 현재 사용자 ID 가져오기
const getCurrentUserId = async () => {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    
    if (!userId) {
      console.log('⚠️ 로그인된 사용자 없음');
      return null;
    }
    
    return userId;
  } catch (error) {
    console.error('❌ 사용자 ID 가져오기 실패:', error);
    return null;
  }
};

// 학습 통계 가져오기
export const fetchLearningStats = async () => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) return null;
    
    const { data, error } = await supabase
      .from('user_learning_stats')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error) throw error;
    
    // 데이터가 없으면 기본 데이터 생성
    if (!data) {
      return await createDefaultStats(userId);
    }
    
    return data;
  } catch (error) {
    console.error('학습 통계 가져오기 실패:', error);
    return null;
  }
};

// 기본 학습 통계 생성
const createDefaultStats = async (userId) => {
  try {
    const defaultStats = {
      user_id: userId,
      total_questions: 0,
      correct_count: 0,
      wrong_count: 0,
      total_study_time: 0,
      recent_study_days: Array(7).fill(0)
    };
    
    const { data, error } = await supabase
      .from('user_learning_stats')
      .insert(defaultStats)
      .select()
      .single();
      
    if (error) throw error;
    
    return data;
  } catch (error) {
    console.error('기본 학습 통계 생성 실패:', error);
    return null;
  }
};

// 정답/오답 처리 후 통계 업데이트 (배치 처리 적용)
export const updateQuizStats = async (isCorrect) => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) return false;
    
    // 배치 처리 대기열에 추가
    pendingUpdates.push({
      userId,
      type: 'quiz',
      data: { isCorrect },
      timestamp: new Date().toISOString()
    });
    
    // 배치 처리 예약
    scheduleBatchProcessing();
    
    return true;
  } catch (error) {
    console.error('퀴즈 통계 업데이트 실패:', error);
    return false;
  }
};

// 학습 시간 업데이트 (배치 처리 적용)
export const updateStudyTime = async (minutesSpent) => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) return false;
    
    // 배치 처리 대기열에 추가
    pendingUpdates.push({
      userId,
      type: 'study_time',
      data: { minutes: minutesSpent },
      timestamp: new Date().toISOString()
    });
    
    // 배치 처리 예약
    scheduleBatchProcessing();
    
    return true;
  } catch (error) {
    console.error('학습 시간 업데이트 실패:', error);
    return false;
  }
};

// 하루 학습 활동 기록 (직접 업데이트)
export const recordDailyActivity = async () => {
  try {
    const userId = await getCurrentUserId();
    if (!userId) return false;
    
    // 기존 데이터 가져오기
    let statsData = await fetchLearningStats();
    if (!statsData) return false;
    
    // 오늘의 학습 기록 업데이트
    const today = new Date().getDay(); // 0(일) ~ 6(토)
    let recentDays = [];
    
    try {
      recentDays = Array.isArray(statsData.recent_study_days) 
        ? [...statsData.recent_study_days] 
        : Array(7).fill(0);
    } catch (e) {
      console.warn('recent_study_days 파싱 오류, 기본값 사용:', e);
      recentDays = Array(7).fill(0);
    }
    
    // 오늘 학습량 증가
    recentDays[today] = (recentDays[today] || 0) + 1;
    
    // 데이터베이스 업데이트
    const { error } = await supabase
      .from('user_learning_stats')
      .update({
        recent_study_days: recentDays,
        updated_at: new Date().toISOString()
      })
      .eq('user_id', userId);
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('일일 학습 활동 기록 실패:', error);
    return false;
  }
}; 